import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Switch,
  Box,
  CircularProgress,
  Divider,
  Tooltip,
  Alert,
  Paper,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import axios from "axios";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDLoading from "components/MDLoading/MDLoading";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Icons
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LanIcon from "@mui/icons-material/Lan";

function SmartlightGroups() {
  const { enqueueSnackbar } = useSnackbar();
  const auth = localStorage.getItem("authToken");
  const userId = localStorage.getItem("user_id") || "1";

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [smartlights, setSmartlights] = useState([]);

  // Create Group Modal
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Edit Group Modal
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editGroupDescription, setEditGroupDescription] = useState("");
  const [updatingGroup, setUpdatingGroup] = useState(false);

  // Add Member Modal
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [selectedGroupForMember, setSelectedGroupForMember] = useState(null);
  const [selectedSmartlightId, setSelectedSmartlightId] = useState("");
  const [addingMember, setAddingMember] = useState(false);

  // Quick Request to Group Modal
  const [groupRequestDialogOpen, setGroupRequestDialogOpen] = useState(false);
  const [targetGroupForRequest, setTargetGroupForRequest] = useState(null);
  const [reqRed, setReqRed] = useState(true);
  const [reqGreen, setReqGreen] = useState(true);
  const [reqWhite, setReqWhite] = useState(true);
  const [reqYellow, setReqYellow] = useState(true);
  const [reqState, setReqState] = useState("REQUEST_STATE");
  const [sendingRequest, setSendingRequest] = useState(false);

  // Delete Confirmation Modal
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);

  useEffect(() => {
    fetchGroupsData();
  }, []);

  const fetchGroupsData = async () => {
    setLoading(true);
    try {
      // 1. Fetch all groups
      const groupsRes = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/all`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (groupsRes.data?.success && Array.isArray(groupsRes.data.groups)) {
        setGroups(groupsRes.data.groups);
      } else {
        setGroups([]);
      }

      // 2. Fetch available smartlights
      const lightsRes = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/available-smartlights`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (lightsRes.data?.success && Array.isArray(lightsRes.data.smartlights)) {
        setSmartlights(lightsRes.data.smartlights);
      } else {
        setSmartlights([]);
      }
    } catch (err) {
      console.error("Error fetching smartlight groups data:", err);
      enqueueSnackbar("Error fetching groups or smartlights", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- Create Group Handler ---
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      enqueueSnackbar("Please enter a group name", { variant: "warning" });
      return;
    }

    setCreatingGroup(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/create`,
        {
          group_name: newGroupName.trim(),
          description: newGroupDescription.trim(),
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (res.data?.success) {
        enqueueSnackbar(res.data.message || "Group created successfully!", { variant: "success" });
        setCreateDialogOpen(false);
        setNewGroupName("");
        setNewGroupDescription("");
        fetchGroupsData();
      } else {
        enqueueSnackbar(res.data?.message || "Failed to create group", { variant: "error" });
      }
    } catch (err) {
      console.error("Create group error:", err);
      enqueueSnackbar("Server error creating group", { variant: "error" });
    } finally {
      setCreatingGroup(false);
    }
  };

  // --- Edit Group Handler ---
  const handleOpenEdit = (group) => {
    setEditGroupId(group.group_id);
    setEditGroupName(group.group_name);
    setEditGroupDescription(group.description || "");
    setEditDialogOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editGroupName.trim()) {
      enqueueSnackbar("Please enter a group name", { variant: "warning" });
      return;
    }

    setUpdatingGroup(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/update`,
        {
          group_id: editGroupId,
          group_name: editGroupName.trim(),
          description: editGroupDescription.trim(),
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (res.data?.success) {
        enqueueSnackbar("Group updated successfully!", { variant: "success" });
        setEditDialogOpen(false);
        fetchGroupsData();
      } else {
        enqueueSnackbar(res.data?.message || "Failed to update group", { variant: "error" });
      }
    } catch (err) {
      console.error("Update group error:", err);
      enqueueSnackbar("Server error updating group", { variant: "error" });
    } finally {
      setUpdatingGroup(false);
    }
  };

  // --- Delete Group Handler ---
  const handleOpenDelete = (group) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    setDeletingGroup(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/delete`,
        {
          group_id: groupToDelete.group_id,
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (res.data?.success) {
        enqueueSnackbar("Group deleted and member lights are now disassociated", {
          variant: "success",
        });
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
        fetchGroupsData();
      } else {
        enqueueSnackbar(res.data?.message || "Failed to delete group", { variant: "error" });
      }
    } catch (err) {
      console.error("Delete group error:", err);
      enqueueSnackbar("Server error deleting group", { variant: "error" });
    } finally {
      setDeletingGroup(false);
    }
  };

  // --- Add Member to Group Handler ---
  const handleOpenAddMember = (group) => {
    setSelectedGroupForMember(group);
    setSelectedSmartlightId("");
    setAddMemberDialogOpen(true);
  };

  const handleAddMember = async () => {
    if (!selectedSmartlightId) {
      enqueueSnackbar("Please select a smartlight", { variant: "warning" });
      return;
    }

    // Client-side guard for already grouped light
    const chosenDev = smartlights.find((d) => String(d.device_id) === String(selectedSmartlightId));
    if (chosenDev?.is_grouped) {
      enqueueSnackbar(
        `Smartlight is already in group "${chosenDev.assigned_group_name}". Remove it from that group first.`,
        { variant: "error" }
      );
      return;
    }

    setAddingMember(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/add-member`,
        {
          group_id: selectedGroupForMember.group_id,
          device_id: selectedSmartlightId,
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (res.data?.success) {
        enqueueSnackbar(res.data.message || "Smartlight added to group successfully!", {
          variant: "success",
        });
        setAddMemberDialogOpen(false);
        setSelectedSmartlightId("");
        fetchGroupsData();
      } else {
        enqueueSnackbar(res.data?.message || "Cannot add smartlight to group", { variant: "error" });
      }
    } catch (err) {
      console.error("Add member error:", err);
      enqueueSnackbar("Server error adding smartlight to group", { variant: "error" });
    } finally {
      setAddingMember(false);
    }
  };

  // --- Remove Member from Group Handler ---
  const handleRemoveMember = async (groupId, deviceId, deviceName) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/remove-member`,
        {
          group_id: groupId,
          device_id: deviceId,
          user_id: userId,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (res.data?.success) {
        enqueueSnackbar(
          `Smartlight "${deviceName}" removed from group. It can now be added to another group.`,
          { variant: "success" }
        );
        fetchGroupsData();
      } else {
        enqueueSnackbar(res.data?.message || "Failed to remove smartlight from group", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error("Remove member error:", err);
      enqueueSnackbar("Server error removing smartlight from group", { variant: "error" });
    }
  };

  // --- Send Group Request Handler ---
  const handleOpenGroupRequest = (group) => {
    setTargetGroupForRequest(group);
    setReqRed(true);
    setReqGreen(true);
    setReqWhite(true);
    setReqYellow(true);
    setReqState("REQUEST_STATE");
    setGroupRequestDialogOpen(true);
  };

  const handleSendGroupRequest = async () => {
    if (!targetGroupForRequest) return;
    setSendingRequest(true);

    const payload = {
      group_id: targetGroupForRequest.group_id,
      RED_LED: reqRed ? "ON" : "OFF",
      GREEN_LED: reqGreen ? "ON" : "OFF",
      WHITE_LED: reqWhite ? "ON" : "OFF",
      YELLOW_LED: reqYellow ? "ON" : "OFF",
      ledr: reqRed ? "ON" : "OFF",
      ledg: reqGreen ? "ON" : "OFF",
      ledw: reqWhite ? "ON" : "OFF",
      ledy: reqYellow ? "ON" : "OFF",
      state: reqState,
      user_id: userId,
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/groups/senddata`,
        payload,
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (res.data?.success) {
        enqueueSnackbar(
          `Lighting request sent to all ${res.data.device_count || targetGroupForRequest.device_count} smartlights in group "${targetGroupForRequest.group_name}"!`,
          { variant: "success" }
        );
        setGroupRequestDialogOpen(false);
        setTimeout(fetchGroupsData, 1000);
      } else {
        enqueueSnackbar(res.data?.message || "Failed to send request to group", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error("Send group request error:", err);
      enqueueSnackbar("Server error sending request to group", { variant: "error" });
    } finally {
      setSendingRequest(false);
    }
  };

  // Available smartlights summary count
  const totalGroupedLights = smartlights.filter((d) => d.is_grouped).length;
  const totalUngroupedLights = smartlights.filter((d) => !d.is_grouped).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={3} pb={4}>
        {/* Top Header Card */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={7}>
              <MDBox display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="3.5rem"
                  height="3.5rem"
                  borderRadius="xl"
                  bgColor="info"
                  color="white"
                  shadow="md"
                  mr={2}
                >
                  <GroupWorkIcon fontSize="large" />
                </MDBox>
                <MDBox>
                  <MDTypography variant="h4" fontWeight="bold">
                    Smartlight Groups
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Admin grouping for <b>smartlights only</b>. Light requests sent to a group will reach all smartlights in that group.
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={5}>
              <MDBox display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1.5}>
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={fetchGroupsData}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => setCreateDialogOpen(true)}
                  startIcon={<AddCircleOutlineIcon />}
                >
                  Create Group
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>

          {/* Quick Metric Pills */}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <LanIcon color="info" fontSize="medium" />
                <Box>
                  <MDTypography variant="caption" color="text" textTransform="uppercase" fontWeight="medium">
                    Total Groups
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold">
                    {groups.length}
                  </MDTypography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleIcon color="success" fontSize="medium" />
                <Box>
                  <MDTypography variant="caption" color="text" textTransform="uppercase" fontWeight="medium">
                    Grouped Smartlights
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold">
                    {totalGroupedLights}
                  </MDTypography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <LightbulbIcon color="warning" fontSize="medium" />
                <Box>
                  <MDTypography variant="caption" color="text" textTransform="uppercase" fontWeight="medium">
                    Available / Ungrouped
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold">
                    {totalUngroupedLights}
                  </MDTypography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Card>

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress color="info" />
          </MDBox>
        ) : (
          <>
            {/* Groups Grid */}
            <Grid container spacing={3}>
              {groups.map((grp) => {
                const memberList = Array.isArray(grp.devices) ? grp.devices : [];
                return (
                  <Grid item xs={12} lg={6} key={grp.group_id}>
                    <Card
                      sx={{
                        p: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                        borderRadius: "16px",
                      }}
                    >
                      <MDBox>
                        {/* Group Header */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                          <MDBox>
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography variant="h5" fontWeight="bold">
                                {grp.group_name}
                              </MDTypography>
                              <Chip
                                label={`${grp.device_count || memberList.length} Smartlights`}
                                color={memberList.length > 0 ? "info" : "default"}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </MDBox>
                            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                              {grp.description || "No description provided."}
                            </MDTypography>
                          </MDBox>

                          <MDBox display="flex" gap={0.5}>
                            <Tooltip title="Edit Group Name">
                              <IconButton size="small" color="info" onClick={() => handleOpenEdit(grp)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Group">
                              <IconButton size="small" color="error" onClick={() => handleOpenDelete(grp)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </MDBox>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Member Smartlights List */}
                        <MDTypography variant="button" fontWeight="bold" color="text" textTransform="uppercase" mb={1} display="block">
                          Assigned Smartlights ({memberList.length}):
                        </MDTypography>

                        {memberList.length === 0 ? (
                          <Alert severity="info" sx={{ my: 1, borderRadius: 2 }}>
                            No smartlights assigned yet. Click <b>+ Add Smartlight</b> below to add available smartlights.
                          </Alert>
                        ) : (
                          <MDBox display="flex" flexDirection="column" gap={1.2} mb={2}>
                            {memberList.map((dev) => {
                              const tele = dev.telemetry || {};
                              return (
                                <Paper
                                  key={dev.device_id}
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    borderRadius: "10px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    backgroundColor: "#f9fafb",
                                  }}
                                >
                                  <MDBox display="flex" alignItems="center" gap={1.5}>
                                    <LightbulbIcon
                                      sx={{
                                        color: dev.light_status === "1" ? "#fbc02d" : "#9e9e9e",
                                        filter: dev.light_status === "1" ? "drop-shadow(0 0 6px rgba(251, 192, 45, 0.6))" : "none",
                                      }}
                                    />
                                    <MDBox>
                                      <MDTypography variant="button" fontWeight="bold" display="block">
                                        {dev.device_name}
                                      </MDTypography>
                                      <MDTypography variant="caption" color="text">
                                        ID #{dev.device_id} &bull; {dev.area_name || "General"} &bull; Type: {dev.device_type}
                                      </MDTypography>
                                      {/* LED Status Chips */}
                                      <MDBox display="flex" gap={0.5} mt={0.5}>
                                        <Chip
                                          label="R"
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: "10px",
                                            fontWeight: "bold",
                                            backgroundColor: tele.RED_LED === "ON" ? "#ef5350" : "#e0e0e0",
                                            color: tele.RED_LED === "ON" ? "#fff" : "#757575",
                                          }}
                                        />
                                        <Chip
                                          label="G"
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: "10px",
                                            fontWeight: "bold",
                                            backgroundColor: tele.GREEN_LED === "ON" ? "#66bb6a" : "#e0e0e0",
                                            color: tele.GREEN_LED === "ON" ? "#fff" : "#757575",
                                          }}
                                        />
                                        <Chip
                                          label="W"
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: "10px",
                                            fontWeight: "bold",
                                            backgroundColor: tele.WHITE_LED === "ON" ? "#42a5f5" : "#e0e0e0",
                                            color: tele.WHITE_LED === "ON" ? "#fff" : "#757575",
                                          }}
                                        />
                                        <Chip
                                          label="Y"
                                          size="small"
                                          sx={{
                                            height: 18,
                                            fontSize: "10px",
                                            fontWeight: "bold",
                                            backgroundColor: tele.YELLOW_LED === "ON" ? "#ffa726" : "#e0e0e0",
                                            color: tele.YELLOW_LED === "ON" ? "#fff" : "#757575",
                                          }}
                                        />
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>

                                  <Tooltip title="Remove from this group (frees light to be added to another group)">
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={() => handleRemoveMember(grp.group_id, dev.device_id, dev.device_name)}
                                    >
                                      <RemoveCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Paper>
                              );
                            })}
                          </MDBox>
                        )}
                      </MDBox>

                      {/* Group Action Buttons */}
                      <MDBox pt={2} display="flex" justifyContent="space-between" gap={1}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => handleOpenAddMember(grp)}
                          startIcon={<AddCircleOutlineIcon />}
                        >
                          Add Smartlight
                        </MDButton>

                        <MDButton
                          variant="gradient"
                          color="success"
                          size="small"
                          disabled={memberList.length === 0}
                          onClick={() => handleOpenGroupRequest(grp)}
                          startIcon={<SendIcon />}
                        >
                          Send Group Request
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Ungrouped Smartlights Section */}
            <Card sx={{ mt: 4, p: 3, borderRadius: "16px" }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold">
                    Available / Ungrouped Smartlights ({totalUngroupedLights})
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Smartlights currently not assigned to any group. Each light can belong to exactly one group.
                  </MDTypography>
                </MDBox>
              </MDBox>

              {totalUngroupedLights === 0 ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  All smartlights are currently assigned to groups!
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {smartlights
                    .filter((dev) => !dev.is_grouped)
                    .map((dev) => (
                      <Grid item xs={12} sm={6} md={4} key={dev.device_id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#fffdf9",
                            borderColor: "#ffe082",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1.5}>
                            <LightbulbIcon color="warning" />
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold">
                                {dev.device_name}
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                ID #{dev.device_id} &bull; {dev.area_name || "General"}
                              </MDTypography>
                              <Chip label="Ready to Group" size="small" color="warning" sx={{ mt: 0.5, height: 20, fontSize: "11px" }} />
                            </MDBox>
                          </MDBox>
                        </Paper>
                      </Grid>
                    ))}
                </Grid>
              )}
            </Card>
          </>
        )}
      </MDBox>

      {/* --- Dialog: Create New Group --- */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Create Smartlight Group</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={2}>
            Create an exclusive group for smartlights only. Once created, you can add smartlights and send group-wide commands.
          </MDTypography>
          <TextField
            label="Group Name"
            placeholder="e.g. North Zone Streetlights"
            fullWidth
            margin="normal"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            required
          />
          <TextField
            label="Description"
            placeholder="e.g. Smart poles along the main entrance and north corridor"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={newGroupDescription}
            onChange={(e) => setNewGroupDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setCreateDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleCreateGroup} variant="gradient" color="info" disabled={creatingGroup}>
            {creatingGroup ? <CircularProgress size={20} color="inherit" /> : "Create Group"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Dialog: Edit Group --- */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Edit Smartlight Group</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Group Name"
            fullWidth
            margin="normal"
            value={editGroupName}
            onChange={(e) => setEditGroupName(e.target.value)}
            required
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={editGroupDescription}
            onChange={(e) => setEditGroupDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleUpdateGroup} variant="gradient" color="info" disabled={updatingGroup}>
            {updatingGroup ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Dialog: Add Smartlight to Group --- */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Add Smartlight to "{selectedGroupForMember?.group_name}"
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <b>Requirement:</b> Only smartlights can be grouped. A smartlight can only belong to <b>one group at a time</b>.
            If a light is already in another group, it must first be removed from that group before adding it here.
          </Alert>

          <TextField
            select
            label="Select Smartlight"
            fullWidth
            margin="normal"
            value={selectedSmartlightId}
            onChange={(e) => setSelectedSmartlightId(e.target.value)}
            helperText="Only smartlight devices are listed. Cameras without lights are excluded."
          >
            <MenuItem value="" disabled>
              -- Select a Smartlight --
            </MenuItem>
            {smartlights.map((dev) => {
              const isOccupied = dev.is_grouped;
              return (
                <MenuItem
                  key={dev.device_id}
                  value={dev.device_id}
                  disabled={isOccupied}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    opacity: isOccupied ? 0.6 : 1,
                  }}
                >
                  <span>
                    {dev.device_name} (ID #{dev.device_id}) &mdash; {dev.device_type}
                  </span>
                  {isOccupied && (
                    <Chip
                      label={`Already in "${dev.assigned_group_name}"`}
                      size="small"
                      color="error"
                      sx={{ ml: 2, height: 20, fontSize: "10px" }}
                    />
                  )}
                </MenuItem>
              );
            })}
          </TextField>

          {selectedSmartlightId && (
            <Box mt={2}>
              {(() => {
                const found = smartlights.find((d) => String(d.device_id) === String(selectedSmartlightId));
                if (found?.is_grouped) {
                  return (
                    <Alert severity="error">
                      This light is currently in group <b>"{found.assigned_group_name}"</b>. You must remove it from that group first before adding it to "{selectedGroupForMember?.group_name}".
                    </Alert>
                  );
                }
                return (
                  <Alert severity="success">
                    Smartlight <b>{found?.device_name}</b> is available and ready to be assigned.
                  </Alert>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setAddMemberDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleAddMember}
            variant="gradient"
            color="info"
            disabled={addingMember || !selectedSmartlightId}
          >
            {addingMember ? <CircularProgress size={20} color="inherit" /> : "Add to Group"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Dialog: Send Group Request --- */}
      <Dialog open={groupRequestDialogOpen} onClose={() => setGroupRequestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Send Lighting Request to Group: {targetGroupForRequest?.group_name}
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={2}>
            This command will be sent simultaneously to all <b>{targetGroupForRequest?.device_count} smartlights</b> in this group via MQTT.
          </MDTypography>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
            <MDTypography variant="subtitle2" fontWeight="bold" mb={1.5}>
              LED Channel Controls:
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="space-between" p={1} bgcolor="#ffebee" borderRadius={1}>
                  <MDTypography variant="button" fontWeight="bold" color="error">
                    Red LED
                  </MDTypography>
                  <Switch checked={reqRed} onChange={(e) => setReqRed(e.target.checked)} color="error" />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="space-between" p={1} bgcolor="#e8f5e9" borderRadius={1}>
                  <MDTypography variant="button" fontWeight="bold" color="success">
                    Green LED
                  </MDTypography>
                  <Switch checked={reqGreen} onChange={(e) => setReqGreen(e.target.checked)} color="success" />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="space-between" p={1} bgcolor="#e3f2fd" borderRadius={1}>
                  <MDTypography variant="button" fontWeight="bold" color="info">
                    White LED
                  </MDTypography>
                  <Switch checked={reqWhite} onChange={(e) => setReqWhite(e.target.checked)} color="info" />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box display="flex" alignItems="center" justifyContent="space-between" p={1} bgcolor="#fff8e1" borderRadius={1}>
                  <MDTypography variant="button" fontWeight="bold" color="warning">
                    Yellow LED
                  </MDTypography>
                  <Switch checked={reqYellow} onChange={(e) => setReqYellow(e.target.checked)} color="warning" />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <TextField
            select
            label="Device Command State"
            fullWidth
            value={reqState}
            onChange={(e) => setReqState(e.target.value)}
          >
            <MenuItem value="REQUEST_STATE">REQUEST_STATE</MenuItem>
            <MenuItem value="ON">ON</MenuItem>
            <MenuItem value="OFF">OFF</MenuItem>
            <MenuItem value="AUTO">AUTO</MenuItem>
            <MenuItem value="DIMMED">DIMMED</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setGroupRequestDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSendGroupRequest}
            variant="gradient"
            color="success"
            disabled={sendingRequest}
            startIcon={<SendIcon />}
          >
            {sendingRequest ? <CircularProgress size={20} color="inherit" /> : "Broadcast to Group"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Dialog: Delete Confirmation --- */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: "bold" }}>Delete Group Confirmation</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2">
            Are you sure you want to delete group <b>"{groupToDelete?.group_name}"</b>?
          </MDTypography>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Deleting this group will disassociate all its smartlights. The lights will remain active in the system and become available to be added into other groups.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteGroup} variant="gradient" color="error" disabled={deletingGroup}>
            {deletingGroup ? <CircularProgress size={20} color="inherit" /> : "Delete Group"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default SmartlightGroups;
